import React, { useState, useEffect } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, Building, Loader2, ChevronDown } from 'lucide-react';
import { amazonIndexCheckerService } from '@/services/amazonIndexCheckerService';
import { DashboardData, DashboardASINData } from '@/types/amazonIndexChecker';
import DashboardMetrics from './DashboardMetrics';
import ASINCard from './ASINCard';

interface DashboardOverviewProps {
  onASINCardClick: (company: string, asin: string) => void;
}

const DashboardOverview: React.FC<DashboardOverviewProps> = ({ onASINCardClick }) => {
  const [dashboardData, setDashboardData] = useState<DashboardData>({
    metrics: {
      totalTermsChecked: 0,
      totalIndexed: 0,
      totalNotIndexed: 0,
      percentageIndexed: 0,
      totalASINs: 0,
      companiesCount: 0,
      lastUpdated: new Date()
    },
    asinsByCompany: {},
    loading: true
  });

  const [refreshing, setRefreshing] = useState(false);
  const [selectedCompanyFilter, setSelectedCompanyFilter] = useState<string>('All Companies');
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, []);

  // Click outside handler to close company dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (showCompanyDropdown && !target.closest('[data-company-dropdown]')) {
        setShowCompanyDropdown(false);
      }
    };

    if (showCompanyDropdown) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showCompanyDropdown]);

  const loadDashboardData = async () => {
    try {
      setDashboardData(prev => ({ ...prev, loading: true, error: undefined }));
      console.log('🚀 Loading dashboard data...');
      
      const data = await amazonIndexCheckerService.getAllASINsWithDataEnhanced();
      setDashboardData(data);
      
      console.log('✅ Dashboard data loaded successfully');
    } catch (error) {
      console.error('❌ Error loading dashboard data:', error);
      setDashboardData(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to load dashboard data'
      }));
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadDashboardData();
    setRefreshing(false);
  };

  const handleASINCardClick = (company: string, asin: string) => {
    console.log(`🔄 Navigating to product view for ${company}/${asin}`);
    onASINCardClick(company, asin);
  };

  const getCompanySortedKeys = () => {
    const allCompanies = Object.keys(dashboardData.asinsByCompany).sort((a, b) => {
      // Sort companies alphabetically
      return a.localeCompare(b);
    });

    // Filter by selected company if not "All Companies"
    if (selectedCompanyFilter === 'All Companies') {
      return allCompanies;
    }
    
    return allCompanies.filter(company => company === selectedCompanyFilter);
  };

  const getCompanyOptions = () => {
    const companies = Object.keys(dashboardData.asinsByCompany).sort((a, b) => a.localeCompare(b));
    return ['All Companies', ...companies];
  };

  const getFilteredMetrics = () => {
    if (selectedCompanyFilter === 'All Companies') {
      return dashboardData.metrics;
    }

    // Calculate metrics for the selected company only
    const companyASINs = dashboardData.asinsByCompany[selectedCompanyFilter] || [];
    const totalTermsChecked = companyASINs.reduce((sum, asin) => sum + asin.termsCount, 0);
    const totalIndexed = companyASINs.reduce((sum, asin) => sum + asin.indexedCount, 0);
    const totalNotIndexed = totalTermsChecked - totalIndexed;
    const percentageIndexed = totalTermsChecked > 0 ? Math.round((totalIndexed / totalTermsChecked) * 100) : 0;

    return {
      ...dashboardData.metrics,
      totalTermsChecked,
      totalIndexed,
      totalNotIndexed,
      percentageIndexed,
      totalASINs: companyASINs.length,
      companiesCount: 1
    };
  };

  if (dashboardData.loading) {
    return (
      <div className="container mx-auto py-6 space-y-6">
        <div className="flex flex-col items-center justify-center min-h-[400px] space-y-6">
          {/* Loading Spinner */}
          <div className="relative">
            <div className="w-16 h-16 border-4 border-blue-200 dark:border-blue-900 rounded-full animate-spin border-t-blue-600 dark:border-t-blue-400"></div>
            <div className="absolute inset-0 w-16 h-16 border-4 border-transparent rounded-full animate-pulse border-t-blue-400 dark:border-t-blue-300" style={{ animationDelay: '0.15s' }}></div>
          </div>
          
          {/* Loading Text */}
          <div className="text-center space-y-2">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Loading Dashboard Data
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Aggregating index data across all companies and ASINs...
            </p>
          </div>
          
          {/* Loading Steps Animation */}
          <div className="flex space-x-2">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="w-2 h-2 bg-blue-600 dark:bg-blue-400 rounded-full animate-pulse"
                style={{ animationDelay: `${i * 0.3}s` }}
              ></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (dashboardData.error) {
    return (
      <div className="container mx-auto py-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {dashboardData.error}
            <button
              onClick={handleRefresh}
              className="ml-4 text-sm underline hover:no-underline"
            >
              Try again
            </button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const companySortedKeys = getCompanySortedKeys();

  if (companySortedKeys.length === 0) {
    return (
      <div className="container mx-auto py-6">
        <DashboardMetrics metrics={dashboardData.metrics} />
        
        <Card className="mt-6">
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <Building className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No Index Data Found
              </h3>
              <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
                Start by checking some terms for your products to see them appear in this dashboard.
              </p>
              <button
                onClick={handleRefresh}
                className="mt-4 text-blue-600 hover:text-blue-800 underline"
              >
                Refresh Dashboard
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Top Metrics */}
      <DashboardMetrics metrics={getFilteredMetrics()} />

      {/* Company Filter Dropdown */}
      <Card className="bg-white dark:bg-gray-800">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Building className="h-5 w-5 text-blue-600" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Filter by Company:
              </span>
            </div>
            <div className="relative" data-company-dropdown>
              <button
                onClick={() => setShowCompanyDropdown(!showCompanyDropdown)}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {selectedCompanyFilter}
                <ChevronDown className={`h-4 w-4 transition-transform ${showCompanyDropdown ? 'rotate-180' : ''}`} />
              </button>
              
              {showCompanyDropdown && (
                <div className="absolute right-0 mt-1 w-48 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg z-10">
                  <div className="py-1">
                    {getCompanyOptions().map((company) => (
                      <button
                        key={company}
                        onClick={() => {
                          setSelectedCompanyFilter(company);
                          setShowCompanyDropdown(false);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-600 ${
                          selectedCompanyFilter === company
                            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                            : 'text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        {company}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Company Sections */}
      <div className="space-y-8">
        {companySortedKeys.map(company => {
          const companyASINs = dashboardData.asinsByCompany[company];
          
          return (
            <div key={company}>
              {/* Company Header */}
              <div className="mb-6">
                <div className="flex items-center gap-2">
                  <Building className="h-6 w-6 text-blue-600" />
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    {company}
                  </h2>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    ({companyASINs.length} ASINs)
                  </span>
                </div>
                <div className="mt-2 h-0.5 bg-gradient-to-r from-blue-600 to-transparent"></div>
              </div>

              {/* ASIN Cards Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                {companyASINs.map(asinData => (
                  <ASINCard
                    key={`${asinData.company}-${asinData.asin}`}
                    asin={asinData}
                    onClick={handleASINCardClick}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer Info */}
      <div className="text-center text-sm text-gray-500 dark:text-gray-400 pt-8 border-t">
        Last updated: {dashboardData.metrics.lastUpdated.toLocaleString()}
        <span className="mx-2">•</span>
        {dashboardData.metrics.totalASINs} ASINs across {dashboardData.metrics.companiesCount} companies
      </div>
    </div>
  );
};

export default DashboardOverview;